import { ChangeEvent } from 'react';

import InputDateRange from '@atom/components/common/inputDateRange/InputDateRange';
import { Icon, Select } from '@atom/mui';
import {
  WorkOrderTemplateFolder,
  WorkTemplate,
} from '@atom/types/workTemplate';
import {
  convertDateToLocalDayEnd,
  convertDateToLocalDayStart,
} from '@atom/utilities/timeUtilities';
import {
  DateRangePresetOption,
  DateRangeTerminator,
} from '@atom/utilities/workOrdersDateFilterUtilities';
import { workOrderStatusTypes } from '@atom/utilities/workOrderStatusUtilities';

import {
  AdditionalFieldFilters,
  FilterForm,
} from '../AssetWorkOrdersFilterPanel';

import TemplateFilter from './TemplateFilter';
import TemplateFolderFilter from './TemplateFolderFilter';
import WorkTemplateWorkFieldsFilter from './WorkTemplateWorkFieldsFilter';

import classes from './../assetWorkOrdersFilterPanel.module.css';

const { MenuItem: SelectMenuItem } = Select;

interface Props {
  schemaId: string;
  areFiltersDisabled: boolean;
  workDueDateSelectValue?: string;
  workDueDateStart?: number;
  workDueDateEnd?: number;
  workCompletionDateSelectValue?: string;
  workCompletionDateStart?: number;
  workCompletionDateEnd?: number;
  workAppliedDateStart?: number;
  workAppliedDateEnd?: number;
  workAppliedDateSelectValue?: string;
  selectStatuses?: number[];
  workTemplateFolders?: WorkOrderTemplateFolder[];
  hasWorkTemplate?: boolean;
  workTemplates?: WorkTemplate[];
  workTemplateFields?: AdditionalFieldFilters;
  workOrderTemplate?: WorkTemplate;
  updateFilterFormData: (field: keyof FilterForm, updatedVal: any) => void;
}

const WorkFiltersSection = ({
  schemaId,
  areFiltersDisabled,
  workDueDateSelectValue,
  workDueDateStart,
  workDueDateEnd,
  workCompletionDateSelectValue,
  workCompletionDateStart,
  workCompletionDateEnd,
  workAppliedDateStart,
  workAppliedDateEnd,
  workAppliedDateSelectValue,
  selectStatuses,
  workTemplateFolders,
  hasWorkTemplate,
  workTemplates,
  workTemplateFields,
  workOrderTemplate,
  updateFilterFormData,
}: Props) => {
  const handleStatusSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value },
    } = event;
    updateFilterFormData('selectStatuses', value);
  };

  const handleInputDateRangeChange = (
    dateValue: Date,
    terminator: DateRangeTerminator,
    formDataKey: keyof FilterForm,
  ) => {
    const dateMillis = dateValue
      ? terminator === DateRangeTerminator.START
        ? convertDateToLocalDayStart(dateValue)
        : convertDateToLocalDayEnd(dateValue)
      : null;
    updateFilterFormData(formDataKey, dateMillis);
  };

  const handleInputDateRangeSelectChange = (
    formDataKey: keyof FilterForm,
    option: DateRangePresetOption,
  ) => {
    updateFilterFormData(formDataKey, option.value);
  };

  return (
    <div className={classes.filterSection}>
      <div className={classes.filterSectionHeader}>
        <Icon>work</Icon>
        Work
      </div>
      <div className={classes.filterContainer}>
        <InputDateRange
          label="Due Date"
          disabled={areFiltersDisabled}
          presetSelected={workDueDateSelectValue}
          handleSelectDatePreset={(option: DateRangePresetOption) =>
            handleInputDateRangeSelectChange('workDueDateSelectValue', option)
          }
          startDateValue={workDueDateStart ? new Date(workDueDateStart) : null}
          handleStartChange={(dateValue: Date) =>
            handleInputDateRangeChange(
              dateValue,
              DateRangeTerminator.START,
              'workDueDateStart',
            )
          }
          endDateValue={workDueDateEnd ? new Date(workDueDateEnd) : null}
          handleEndChange={(dateValue: Date) =>
            handleInputDateRangeChange(
              dateValue,
              DateRangeTerminator.END,
              'workDueDateEnd',
            )
          }
        />
      </div>
      <div className={classes.filterContainer}>
        <InputDateRange
          label="Completed Date"
          disabled={areFiltersDisabled}
          presetSelected={workCompletionDateSelectValue}
          handleSelectDatePreset={(option: DateRangePresetOption) =>
            handleInputDateRangeSelectChange(
              'workCompletionDateSelectValue',
              option,
            )
          }
          startDateValue={
            workCompletionDateStart ? new Date(workCompletionDateStart) : null
          }
          handleStartChange={(dateValue: Date) =>
            handleInputDateRangeChange(
              dateValue,
              DateRangeTerminator.START,
              'workCompletionDateStart',
            )
          }
          endDateValue={
            workCompletionDateEnd ? new Date(workCompletionDateEnd) : null
          }
          handleEndChange={(dateValue: Date) =>
            handleInputDateRangeChange(
              dateValue,
              DateRangeTerminator.END,
              'workCompletionDateEnd',
            )
          }
        />
      </div>
      <div className={classes.filterContainer}>
        <InputDateRange
          label="Inventory Update Date"
          disabled={areFiltersDisabled}
          presetSelected={workAppliedDateSelectValue}
          handleSelectDatePreset={(option: DateRangePresetOption) =>
            handleInputDateRangeSelectChange(
              'workAppliedDateSelectValue',
              option,
            )
          }
          startDateValue={
            workAppliedDateStart ? new Date(workAppliedDateStart) : null
          }
          handleStartChange={(dateValue: Date) =>
            handleInputDateRangeChange(
              dateValue,
              DateRangeTerminator.START,
              'workAppliedDateStart',
            )
          }
          endDateValue={
            workAppliedDateEnd ? new Date(workAppliedDateEnd) : null
          }
          handleEndChange={(dateValue: Date) =>
            handleInputDateRangeChange(
              dateValue,
              DateRangeTerminator.END,
              'workAppliedDateEnd',
            )
          }
        />
      </div>
      <div className={classes.filterContainer}>
        <div className={classes.filterLabel}>Work Status</div>
        <Select
          multiple
          displayEmpty
          value={selectStatuses || []}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            handleStatusSelect(event)
          }
          disabled={areFiltersDisabled}
          renderValue={selectStatuses.length > 0 ? null : () => 'All'}
        >
          {workOrderStatusTypes.map(status => (
            <SelectMenuItem key={+status.value} value={+status.value}>
              {status.label}
            </SelectMenuItem>
          ))}
        </Select>
      </div>
      <TemplateFolderFilter
        schemaId={schemaId}
        areFiltersDisabled={areFiltersDisabled}
        workTemplateFolderFilter={workTemplateFolders}
        updateWorkTemplateFolderFilter={(
          templateFolders: WorkOrderTemplateFolder[],
        ) => updateFilterFormData('workTemplateFolders', templateFolders)}
      />
      <TemplateFilter
        schemaId={schemaId}
        areFiltersDisabled={areFiltersDisabled}
        hasWorkTemplateFilter={hasWorkTemplate}
        updateHasWorkTemplateFilter={(hasWorkTemplateUpdate: boolean) =>
          updateFilterFormData('hasWorkTemplate', hasWorkTemplateUpdate)
        }
        workTemplateFilter={workTemplates}
        updateWorkTemplateFilter={(workTemplatesUpdate: WorkTemplate[]) =>
          updateFilterFormData('workTemplates', workTemplatesUpdate)
        }
      />
      {workTemplates.length === 1 && workOrderTemplate && (
        <WorkTemplateWorkFieldsFilter
          areFiltersDisabled={areFiltersDisabled}
          workOrderTemplate={workOrderTemplate}
          fieldFilters={workTemplateFields}
          updateFieldFilters={fieldFiltersUpdate =>
            updateFilterFormData('workTemplateFields', fieldFiltersUpdate)
          }
        />
      )}
    </div>
  );
};

export default WorkFiltersSection;
